import { ProjectSpecificFields } from '@ors/components/manage/Blocks/ProjectsListing/interfaces'
import { formatFieldLabel } from '@ors/components/manage/Blocks/ProjectsListing/utils'
import { defaultSliceData } from './createYearRangesSlice'
import type { CreateSliceProps, ProjectsFieldsSlice } from '@ors/types/store'
import { fetchSliceData } from '@ors/helpers/Store/Store'

import { filter } from 'lodash'
import { produce } from 'immer'

export const createProjectFieldsSlice = ({
  get,
  set,
}: CreateSliceProps): ProjectsFieldsSlice => {
  return {
    projectFields: defaultSliceData,
    viewableFields: [],
    editableFields: [],
    setViewableFields: (version: number, submissionStatus?: string) => {
      const fields = get().projectFields.projectFields?.data ?? []

      const viewableFields = filter(fields, ({ visible_in_versions }) => {
        const versionCheck = version >= 3 ? 3 : version
        return (
          visible_in_versions?.includes(versionCheck) &&
          (submissionStatus !== 'Draft' || visible_in_versions.includes(1))
        )
      }).map((field) => field.write_field_name)

      set(
        produce((state) => {
          state.projectFields.viewableFields = viewableFields
        }),
      )
    },
    setEditableFields: (
      version: number,
      submissionStatus?: string,
      canEditAll?: boolean,
      isPostExcom?: boolean,
      mode?: string,
    ) => {
      const fields = get().projectFields.projectFields?.data ?? []
      const isAfterApproval = ['Approved', 'Not approved'].includes(
        submissionStatus ?? '',
      )

      const editableFields = fields
        .filter(
          ({ editable_in_versions, is_actual, section, write_field_name }) => {
            if (section === 'MYA') {
              return false
            }

            const isFieldNotV3Editable = [
              'bp_activity',
              'blanket_or_individual_consideration',
            ].includes(write_field_name)

            if (
              mode === 'edit' &&
              submissionStatus === 'Approved' &&
              !isPostExcom &&
              !canEditAll
            ) {
              return is_actual
            }

            if (!isFieldNotV3Editable) {
              if (
                mode === 'edit' &&
                submissionStatus === 'Approved' &&
                !isPostExcom
              ) {
                return (
                  section !== 'Approval' && (section !== 'Impact' || is_actual)
                )
              }

              if (mode === 'edit' && submissionStatus === 'Not approved') {
                return section !== 'Approval'
              }

              const postExcomEditableApprovalFields = [
                'programme_officer',
                'excom_provision',
                'ad_hoc_pcr',
                'pcr_waived',
              ]

              if (isPostExcom) {
                return (
                  (section !== 'Approval' ||
                    postExcomEditableApprovalFields.includes(
                      write_field_name,
                    )) &&
                  (section !== 'Impact' || is_actual)
                )
              }
            }

            const isEditableInVersion = editable_in_versions?.includes(version)
            const isFieldEditable =
              section !== 'Impact' || !isAfterApproval || is_actual
            const isDraftEditable =
              submissionStatus !== 'Draft' || editable_in_versions?.includes(1)
            const isEditableByStatus =
              write_field_name !== 'bp_activity' ||
              submissionStatus !== 'Withdrawn'
            const canEditField =
              isEditableInVersion &&
              isFieldEditable &&
              isDraftEditable &&
              isEditableByStatus

            return !isFieldNotV3Editable
              ? canEditAll || canEditField
              : canEditField
          },
        )
        .map((field) => field.write_field_name)

      set(
        produce((state) => {
          state.projectFields.editableFields = editableFields
        }),
      )
    },
    fetchProjectFields: async () => {
      const fields = await fetchSliceData({
        apiSettings: { path: 'api/project-fields/' },
        slice: 'projectFields.projectFields',
      })

      set(
        produce((state) => {
          const formattedFields = (fields || []).map(
            (field: ProjectSpecificFields) => ({
              ...field,
              label: formatFieldLabel(field.label),
            }),
          )
          state.projectFields.projectFields = formattedFields
        }),
      )
    },
  }
}
